import { zodResolver } from '@hookform/resolvers/zod'
import { type LessonInput, LessonSchema } from '@lms/shared'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DirectVideoUploader } from '@/components/ui/direct-video-uploader'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { TagInput } from '@/components/ui/tag-input'
import { Textarea } from '@/components/ui/textarea'
import { useCreateLesson, useLesson, useUpdateLesson } from '@/hooks/use-lessons'
import { useCreateTag, useTags } from '@/hooks/use-tags'
import { useTenantPath } from '@/lib/tenant'

const LESSON_LEVELS = [
  { value: 'ENTRY', label: 'エントリー' },
  { value: 'BASIC', label: 'ベーシック' },
  { value: 'ADVANCED', label: 'アドバンス' },
  { value: 'EXPERT', label: 'エキスパート' },
] as const

const LESSON_TYPES = [
  { value: 'video', label: '動画' },
  { value: 'external', label: '外部研修' },
] as const

export default function LessonFormPage() {
  const { id, courseId } = useParams<{ id: string; courseId: string }>()
  const navigate = useNavigate()
  const tp = useTenantPath()
  const isEdit = !!id

  const { data: lesson, isLoading } = useLesson(id!)
  const { data: tagsData } = useTags()
  const createLesson = useCreateLesson()
  const updateLesson = useUpdateLesson()
  const createTag = useCreateTag()

  const [selectedTagId, setSelectedTagId] = useState<string[]>([])

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LessonInput>({
    resolver: zodResolver(LessonSchema),
    defaultValues: {
      title: '',
      description: '',
      type: 'video',
      content: '',
      externalUrl: '',
      level: 'ENTRY',
      downloadable: false,
    },
  })

  const lessonType = watch('type')

  useEffect(() => {
    if (lesson && isEdit) {
      const l = lesson as any
      reset({
        title: l.title,
        description: l.description ?? '',
        type: l.type ?? 'video',
        content: l.content ?? '',
        externalUrl: l.externalUrl ?? '',
        level: l.level ?? 'ENTRY',
        downloadable: l.downloadable ?? false,
      })
      if (l.tagId) {
        setSelectedTagId([l.tagId])
      }
    }
  }, [lesson, isEdit, reset])

  const availableTags = (Array.isArray(tagsData) ? tagsData : []).map((t: any) => ({
    id: t.id,
    name: t.name,
  }))

  const handleCreateTag = async (name: string) => {
    const tag = (await createTag.mutateAsync({ name })) as any
    return { id: tag.id, name: tag.name }
  }

  const onSubmit = async (data: any) => {
    try {
      const tagId = selectedTagId.length > 0 ? selectedTagId[0] : undefined
      const payload = {
        ...data,
        courseId: courseId || data.courseId,
        tagId,
      }
      if (isEdit) {
        await updateLesson.mutateAsync({ id: id!, data: payload })
        toast.success('レッスンを更新しました')
      } else {
        await createLesson.mutateAsync(payload)
        toast.success('レッスンを作成しました')
      }
      navigate(tp('/teacher/lessons'))
    } catch {
      toast.error(isEdit ? '更新に失敗しました' : '作成に失敗しました')
    }
  }

  if (isEdit && isLoading) {
    return <Skeleton className="h-96" />
  }

  const lessonData = lesson as any

  return (
    <div className="max-w-2xl">
      <h1 className="mb-6 text-2xl font-bold">{isEdit ? 'レッスン編集' : 'レッスン作成'}</h1>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>基本情報</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">タイトル</Label>
              <Input id="title" placeholder="例: React Hooks入門" {...register('title')} />
              {errors.title && (
                <p className="text-sm text-destructive">{errors.title.message as string}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">説明</Label>
              <Textarea
                id="description"
                rows={3}
                placeholder="レッスンの概要を入力してください"
                {...register('description')}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message as string}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>レッスン種別</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>種別</Label>
                <Select
                  value={lessonType}
                  onValueChange={(value) => setValue('type', value as 'video' | 'external')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="種別を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {LESSON_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.type && (
                  <p className="text-sm text-destructive">{errors.type.message as string}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>レベル</Label>
                <Select
                  value={watch('level')}
                  onValueChange={(value) =>
                    setValue('level', value as 'ENTRY' | 'BASIC' | 'ADVANCED' | 'EXPERT')
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="レベルを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {LESSON_LEVELS.map((l) => (
                      <SelectItem key={l.value} value={l.value}>
                        {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.level && (
                  <p className="text-sm text-destructive">{errors.level.message as string}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="externalUrl">URL</Label>
              <Input
                id="externalUrl"
                type="url"
                placeholder="https://example.com/training"
                {...register('externalUrl')}
              />
              {errors.externalUrl && (
                <p className="text-sm text-destructive">{errors.externalUrl.message as string}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">テキストコンテンツ</Label>
              <Textarea
                id="content"
                rows={5}
                placeholder="レッスンのテキストコンテンツ（マークダウン対応）"
                {...register('content')}
              />
            </div>
          </CardContent>
        </Card>

        {lessonType === 'video' && (
          <Card>
            <CardHeader>
              <CardTitle>動画</CardTitle>
            </CardHeader>
            <CardContent>
              {isEdit && id ? (
                <DirectVideoUploader
                  lessonId={id}
                  initialVideo={
                    lessonData?.videoBlobName
                      ? {
                          url: '',
                          filename: lessonData.videoOriginalFilename ?? '動画ファイル',
                        }
                      : null
                  }
                  onUploadSuccess={() => {
                    toast.success('動画をアップロードしました')
                  }}
                  onUploadError={(error) => {
                    toast.error(`動画アップロードに失敗しました: ${error}`)
                  }}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  レッスンを作成した後、編集画面から動画をアップロードできます
                </p>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>分類・設定</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>タグ</Label>
              <TagInput
                value={selectedTagId}
                onChange={(ids) => setSelectedTagId(ids.slice(-1))}
                availableTags={availableTags}
                onCreateTag={handleCreateTag}
                placeholder="タグを選択..."
              />
              <p className="text-xs text-muted-foreground">レッスンには1つのタグを設定できます</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="downloadable"
                  checked={watch('downloadable')}
                  onCheckedChange={(checked) => setValue('downloadable', checked)}
                />
                <Label htmlFor="downloadable">ダウンロード許可</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? '保存中...' : isEdit ? '更新' : '作成'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(tp('/teacher/lessons'))}>
            キャンセル
          </Button>
        </div>
      </form>
    </div>
  )
}
